import { ChangeEvent, FC, useCallback, useState } from "react"

import { useTranslation } from "react-i18next"

import { GgrSummarySection } from "components/charts/GgrSummarySection/GgrSummarySection"
import { StatisticChart } from "components/charts/StatisticChart"
import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"
import { WelcomeSection } from "components/general/WelcomeSection"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { MultiSelect } from "components/inputs/MultiSelect"
import { TopBar } from "components/navigation/TopBar"
import { StatCard } from "components/stats/Card"

import { NgrTooltipContent } from "features/user/tooltip/NgrTooltipContent"

import { usePermissions } from "contexts/PermissionsContext"

import { useStatsCardGroups } from "hooks/useStatsCardGroups"
import { useUserCount } from "hooks/useUserCount"

import { tenantOptions } from "constants/bonus"
import { PERMISSIONS } from "constants/permissions"
import { betTypeOptions } from "constants/transactions"

import { EnumTimePeriod, getTimePeriod } from "util/dashboard"

export const Dashboard: FC = () => {
  const [timeframe, setTimeframe] = useState<(Date | null)[]>(getTimePeriod(EnumTimePeriod.today))
  const [tenant, setTenant] = useState<string>("")
  const [betTypes, setBetTypes] = useState<string[]>([])
  const { has } = usePermissions()
  const { t } = useTranslation("translation", { useSuspense: false })
  const canSeeGgrInsight = has(PERMISSIONS.GGR_INSIGHT_VIEW)

  const handleChangeTimeFrame = useCallback((dates: (Date | null)[]) => setTimeframe(dates), [])
  const { loading: loadingUserCount, data: userCount } = useUserCount(timeframe, tenant)
  const { bettingLoading, transactionsLoading, depositorsLoading, data: stats } = useStatsCardGroups(timeframe, tenant, betTypes)

  return (
    <div className="relative w-full">
      <div className="flex flex-col items-start w-full gap-8 p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("pages.dashboard.title")} />
        <WelcomeSection />

        <div className="flex flex-wrap gap-6 pb-2 gap-y-3 justify-start sm:justify-start max-w-full w-full border-b dark:border-[rgba(255,255,255,0.1)] border-[rgba(0,0,0,0.05)] md:flex-row md:gap-6 md:items-end md:pb-4">
          <div className="max-w-md z-20">
            <SingleOptTimeRangePicker noLabel onTimeframeChange={handleChangeTimeFrame} extendedPresetOptions={true} />
          </div>
          <div className="w-[165px]">
            <DropdownMenu
              placeholder={t("form.allTenants")}
              options={tenantOptions}
              value={tenant}
              onChange={(ev: ChangeEvent<HTMLSelectElement>) => setTenant(ev.target.value)}
              rounded
            />
          </div>
          <div className="w-[220px]">
            <MultiSelect
              options={betTypeOptions}
              value={betTypes}
              onChange={setBetTypes}
              allLabel={t("form.betType")}
              buttonClassName="border-gray-200 dark:border-[rgba(255,255,255,0.1)]"
            />
          </div>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,_minmax(200px,_1fr))] gap-5 w-full">
          <StatCard title={t("stats.ggr")} bgColor="bg-yellow-500" value={stats?.ggr ?? 0} loading={bettingLoading} type="currency" />
          <StatCard
            title={t("stats.ngr")}
            bgColor="bg-yellow-500"
            value={stats?.ngr ?? 0}
            loading={bettingLoading || transactionsLoading}
            type="currency"
            infoContent={canSeeGgrInsight ? <NgrTooltipContent inputs={stats?.ngrInputs} wager={stats?.wager} win={stats?.win} /> : undefined}
          />
          <StatCard title={t("stats.registrations")} value={userCount} loading={loadingUserCount} />
          <StatCard
            title={t("stats.depositTotal")}
            bgColor="bg-primary"
            value={stats?.deposit ?? 0}
            loading={transactionsLoading}
            type="currency"
          />
          <StatCard
            title={t("stats.withdrawalCompleted")}
            bgColor="bg-secondary"
            value={stats?.withdrawal ?? 0}
            loading={transactionsLoading}
            type="currency"
          />
          <StatCard
            title={t("stats.betsTotal")}
            bgColor="bg-orange-600"
            value={stats?.wager ?? 0}
            loading={bettingLoading}
            type="currency"
          />
          <StatCard title={t("stats.winsTotal")} bgColor="bg-blue-600" value={stats?.win ?? 0} loading={bettingLoading} type="currency" />
          <StatCard
            title={t("stats.claimedBonus")}
            bgColor="bg-lime-600"
            value={stats?.bonus ?? 0}
            loading={transactionsLoading}
            type="currency"
          />
          <StatCard
            title={t("stats.averageBet")}
            bgColor="bg-primary"
            value={stats?.averageBet ?? 0}
            loading={bettingLoading}
            type="currency"
          />

          <StatCard title={t("stats.activeUsers")} bgColor="bg-orange-300" value={stats?.activeUsers ?? 0} loading={transactionsLoading} />
          <StatCard title={t("stats.ftd")} bgColor="bg-gray-700" value={stats?.depositers ?? 0} loading={depositorsLoading} />

          <StatCard
            title={t("stats.depositCount")}
            bgColor="bg-emerald-500"
            value={stats?.depositCount ?? 0}
            loading={transactionsLoading}
          />
          <StatCard
            title={t("stats.ftdSum")}
            bgColor="bg-indigo-600"
            value={stats?.ftdSum ?? 0}
            loading={depositorsLoading}
            type="currency"
          />
          <StatCard
            title={t("stats.netDeposits")}
            bgColor="bg-cyan-700"
            value={stats?.netDeposits ?? 0}
            loading={transactionsLoading}
            type="currency"
          />
          <StatCard
            title={t("stats.withdrawalCount")}
            bgColor="bg-rose-600"
            value={stats?.withdrawalCount ?? 0}
            loading={transactionsLoading}
          />
        </div>
        <StatisticChart timeframe={timeframe} tenant={tenant} />
        <GgrSummarySection timeframe={timeframe} tenant={tenant} />
      </div>
    </div>
  )
}
